// ProgramStaffMatrix.jsx
// Shows who runs each program - Directors and Coaches

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaClipboardList, FaUserTie, FaChalkboardTeacher, FaArrowRight,
  FaExclamationTriangle, FaCheckCircle, FaExclamationCircle, FaUsers
} from 'react-icons/fa';
import { useUser } from '../../../context/UserContext';
import styles from './ProgramStaffMatrix.module.css';

const ProgramStaffMatrix = ({
  clubId,
  programs,
  loading
}) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [programStaff, setProgramStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);

  // Fetch staff data for each program
  useEffect(() => {
    const fetchProgramStaff = async () => {
      if (!programs || programs.length === 0 || !user) {
        setProgramStaff([]);
        setStaffLoading(false);
        return;
      }

      try {
        setStaffLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        
        // Get auth token
        const token = await user.getIdToken();
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        
        const staffData = await Promise.all(
          programs.map(async (program) => {
            try {
              const response = await fetch(`${backendUrl}/api/programs/${program._id}/members`, { headers });
              if (!response.ok) return { program, director: null, headCoach: null, assistantCoaches: 0, enrollees: 0 };
              
              const members = await response.json();
              const membersList = Array.isArray(members) ? members : members.members || [];
              
              // Find director
              const director = membersList.find(m => 
                m.role === 'ProgramDirector' || m.role === 'programDirector'
              );
              
              // Find head coach
              const headCoach = membersList.find(m => 
                m.role === 'HeadCoach' || m.role === 'headCoach'
              );
              
              // Count assistant coaches
              const assistantCoaches = membersList.filter(m => 
                m.role === 'AssistantCoach' || m.role === 'assistantCoach'
              ).length;
              
              // Count enrolled members
              const enrollees = membersList.filter(m => 
                m.status === 'Enrolled' || m.status === 'enrolled' || m.status === 'active'
              ).length;

              return {
                program,
                director,
                headCoach,
                assistantCoaches,
                enrollees
              };
            } catch (e) {
              return { program, director: null, headCoach: null, assistantCoaches: 0, enrollees: 0 };
            }
          })
        );

        setProgramStaff(staffData);
      } catch (error) {
        console.error('Error fetching program staff:', error);
      } finally {
        setStaffLoading(false);
      }
    };

    fetchProgramStaff();
  }, [programs, user]);

  const getStaffStatus = (item) => {
    if (item.director && (item.headCoach || item.assistantCoaches > 0)) {
      return 'fully-staffed';
    } else if (item.director || item.headCoach || item.assistantCoaches > 0) {
      return 'partial';
    }
    return 'understaffed';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'fully-staffed':
        return <FaCheckCircle className={styles.statusIconGreen} />;
      case 'partial':
        return <FaExclamationCircle className={styles.statusIconYellow} />;
      case 'understaffed':
        return <FaExclamationTriangle className={styles.statusIconRed} />;
      default:
        return null;
    }
  };

  const handleManageProgram = (programId) => {
    navigate(`/clubs/${clubId}/programs/${programId}/manage`);
  };

  const handleAssignStaff = () => {
    navigate(`/clubs/${clubId}`);
  };

  const getPersonName = (person) => {
    if (!person) return '—';
    const user = person.user || person;
    return user.firstName 
      ? `${user.firstName} ${user.lastName || ''}`.trim()
      : user.name || user.email || '—';
  };

  // Calculate summary
  const fullyStaffed = programStaff.filter(p => getStaffStatus(p) === 'fully-staffed').length;
  const partial = programStaff.filter(p => getStaffStatus(p) === 'partial').length;
  const understaffed = programStaff.filter(p => getStaffStatus(p) === 'understaffed').length;

  if (loading || staffLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaClipboardList className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Program Staff Matrix</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaClipboardList className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Program Staff Matrix</h3>
        </div>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>📋</span>
          <p>No programs created yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaClipboardList className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Program Staff Matrix</h3>
        <span className={styles.subtitle}>Who runs each program</span>
      </div>

      <div className={styles.content}>
        {/* Staff Table */}
        <div className={styles.tableWrapper}>
          <table className={styles.staffTable}>
            <thead>
              <tr>
                <th>Program</th>
                <th><FaUserTie /> Director</th>
                <th><FaChalkboardTeacher /> Head Coach</th>
                <th><FaUsers /> Coaches</th>
                <th>Enrollees</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {programStaff.map((item, index) => {
                const status = getStaffStatus(item);
                return (
                  <tr 
                    key={item.program._id || index}
                    className={styles[status]}
                    onClick={() => handleManageProgram(item.program._id)}
                  >
                    <td className={styles.programCell}>
                      <span className={styles.programName}>{item.program.name}</span>
                    </td>
                    <td className={styles.personCell}>
                      {item.director ? (
                        <span className={styles.personName}>{getPersonName(item.director)}</span>
                      ) : (
                        <span className={styles.missing}>Needs Director</span>
                      )}
                    </td>
                    <td className={styles.personCell}>
                      {item.headCoach ? (
                        <span className={styles.personName}>{getPersonName(item.headCoach)}</span>
                      ) : (
                        <span className={styles.missingOptional}>—</span>
                      )}
                    </td>
                    <td className={styles.countCell}>{item.assistantCoaches}</td>
                    <td className={styles.countCell}>
                      <span className={styles.enrolleeBadge}>
                        {item.enrollees}/{item.program.capacity?.maxParticipants || item.program.maxCapacity || '∞'}
                      </span>
                    </td>
                    <td className={styles.statusCell}>
                      {getStatusIcon(status)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <FaCheckCircle className={styles.legendIconGreen} />
            <span>Fully Staffed ({fullyStaffed})</span>
          </div>
          <div className={styles.legendItem}>
            <FaExclamationCircle className={styles.legendIconYellow} />
            <span>Partially Staffed ({partial})</span>
          </div>
          <div className={styles.legendItem}>
            <FaExclamationTriangle className={styles.legendIconRed} />
            <span>Understaffed ({understaffed})</span>
          </div>
        </div>

        {/* Warning Banner */}
        {understaffed > 0 && (
          <div className={styles.warningBanner}>
            <FaExclamationTriangle className={styles.warningIcon} />
            <span>{understaffed} program{understaffed > 1 ? 's' : ''} need{understaffed === 1 ? 's' : ''} a Director assigned</span>
            <button className={styles.assignButton} onClick={handleAssignStaff}>
              Assign Staff
            </button>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button className={styles.footerLink} onClick={() => navigate(`/clubs/${clubId}`)}>
          Manage Programs <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default ProgramStaffMatrix;

